import type {
  HomeParaMiBannerRow,
  HomeParaMiCard,
  HomeParaMiContent,
  HomeParaMiPaso,
} from '../models/home-para-mi-content.model';
import { prepareParaMiTitleHtml } from './home-para-mi-html.util';

export const QUE_ES_NOMBRE_TITULO = 'QUEES_TITULO_SECCION';
export const QUE_ES_NOMBRE_PN_CABECERA = 'QUEES_PN_CABECERA';
export const QUE_ES_NOMBRE_PJ_CABECERA = 'QUEES_PJ_CABECERA';
export const QUE_ES_PREFIJO_PASO_PN = 'QUEES_PN_PASO';
export const QUE_ES_PREFIJO_PASO_PJ = 'QUEES_PJ_PASO';

/** Legacy: pasos semilla viejos (orden 11–29 / 31–49) hasta Guardar o re-seed. */
const QUE_ES_PN_PASO_MIN_LEGACY = 11;
const QUE_ES_PN_PASO_MAX_LEGACY = 29;
const QUE_ES_PJ_PASO_MIN_LEGACY = 31;
const QUE_ES_PJ_PASO_MAX_LEGACY = 49;

function normalizeOrden(row: HomeParaMiBannerRow, index: number): number {
  const orden = row.orden;
  return typeof orden === 'number' && !Number.isNaN(orden) ? orden : index;
}

function nombreArchivoUpper(row: HomeParaMiBannerRow): string {
  return String(row.nombreArchivo ?? '').toUpperCase();
}

function findByNombre(
  rows: HomeParaMiBannerRow[],
  nombre: string
): HomeParaMiBannerRow | undefined {
  const target = nombre.toUpperCase();
  return rows.find((row) => nombreArchivoUpper(row) === target);
}

function esPasoPn(row: HomeParaMiBannerRow): boolean {
  const nombre = nombreArchivoUpper(row);
  if (nombre.startsWith(QUE_ES_PREFIJO_PASO_PN)) {
    return true;
  }
  const orden = normalizeOrden(row, -1);
  return orden >= QUE_ES_PN_PASO_MIN_LEGACY && orden <= QUE_ES_PN_PASO_MAX_LEGACY;
}

function esPasoPj(row: HomeParaMiBannerRow): boolean {
  const nombre = nombreArchivoUpper(row);
  if (nombre.startsWith(QUE_ES_PREFIJO_PASO_PJ)) {
    return true;
  }
  const orden = normalizeOrden(row, -1);
  return orden >= QUE_ES_PJ_PASO_MIN_LEGACY && orden <= QUE_ES_PJ_PASO_MAX_LEGACY;
}

function mapPasoRow(row: HomeParaMiBannerRow): HomeParaMiPaso | null {
  const descripcion = row.contenido?.trim() ?? '';
  if (!descripcion) {
    return null;
  }
  return {
    idBanner: row.idBanner,
    orden: normalizeOrden(row, 0),
    descripcion,
  };
}

function mapCabeceraCard(
  row: HomeParaMiBannerRow | undefined,
  pasos: HomeParaMiPaso[],
  fallback: HomeParaMiCard
): HomeParaMiCard {
  if (!row) {
    return {
      ...fallback,
      pasos: pasos.length ? pasos : fallback.pasos.map((p) => ({ ...p })),
    };
  }

  const title = row.titulo?.trim() || fallback.title;
  const description = row.contenido?.trim() || fallback.description;
  const buttonLabel = row.nombreBoton?.trim() || fallback.buttonLabel;
  const buttonUrl = row.urlForward?.trim() || fallback.buttonUrl;

  return {
    title,
    description,
    buttonLabel,
    buttonUrl,
    pasos: pasos.length ? pasos : fallback.pasos.map((p) => ({ ...p })),
  };
}

/**
 * Mapeo QueEsNeocredito por `nombreArchivo`:
 * - QUEES_TITULO_SECCION → título sección (sin orden)
 * - QUEES_PN_CABECERA / QUEES_PJ_CABECERA → tarjetas (sin orden)
 * - QUEES_PN_PASO_* / QUEES_PJ_PASO_* → pasos (orden 1..N por canal)
 * Legacy: rangos de orden 11–29 (PN) y 31–49 (PJ) si aún no se normalizó.
 */
export function mapQueEsBannersToContent(
  rows: HomeParaMiBannerRow[],
  fallback: HomeParaMiContent
): HomeParaMiContent {
  if (!rows?.length) {
    return fallback;
  }

  const tituloRow = findByNombre(rows, QUE_ES_NOMBRE_TITULO);
  const pnCabecera = findByNombre(rows, QUE_ES_NOMBRE_PN_CABECERA);
  const pjCabecera = findByNombre(rows, QUE_ES_NOMBRE_PJ_CABECERA);

  const pnPasos = [...rows]
    .filter(esPasoPn)
    .sort((a, b) => normalizeOrden(a, 0) - normalizeOrden(b, 0))
    .map(mapPasoRow)
    .filter((paso): paso is HomeParaMiPaso => !!paso);

  const pjPasos = [...rows]
    .filter(esPasoPj)
    .sort((a, b) => normalizeOrden(a, 0) - normalizeOrden(b, 0))
    .map(mapPasoRow)
    .filter((paso): paso is HomeParaMiPaso => !!paso);

  const sectionTitle = prepareParaMiTitleHtml(
    tituloRow?.titulo?.trim() || fallback.sectionTitle
  );

  return {
    sectionTitle,
    natural: mapCabeceraCard(pnCabecera, pnPasos, fallback.natural),
    juridica: mapCabeceraCard(pjCabecera, pjPasos, fallback.juridica),
  };
}

export {
  isExternalParaMiUrl,
  parseParaMiInternalLink,
  type ParaMiInternalLink,
} from './home-para-mi-link.util';
